/**
 * Custom hooks per gestione companies (Admin)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type { CompanyCreate, CompanyListFilters, CompanyResponse, CompanyUpdate, DeleteResponse } from "@/types/api/admin";

const COMPANIES_QUERY_KEYS = {
    all: ["companies"] as const,
    lists: () => [...COMPANIES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: CompanyListFilters) => [...COMPANIES_QUERY_KEYS.lists(), filters] as const,
    details: () => [...COMPANIES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...COMPANIES_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook per lista companies con filtri
 */
export function useCompanies(filters?: CompanyListFilters) {
    return useQuery({
        queryKey: COMPANIES_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<CompanyResponse[]> => {
            return await apiGet<CompanyResponse[]>("/companies", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
    });
}

/**
 * Hook per singola company
 */
export function useCompany(companyId: string) {
    return useQuery({
        queryKey: COMPANIES_QUERY_KEYS.detail(companyId),
        queryFn: async (): Promise<CompanyResponse> => {
            return await apiGet<CompanyResponse>(`/companies/${companyId}`);
        },
        enabled: !!companyId,
    });
}

/**
 * Hook per creare company
 */
export function useCreateCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CompanyCreate): Promise<CompanyResponse> => {
            return await apiPost<CompanyResponse>("/companies", data);
        },
        onSuccess: () => {
            // Invalida lista companies
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per aggiornare company
 */
export function useUpdateCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ companyId, data }: { companyId: string; data: CompanyUpdate }): Promise<CompanyResponse> => {
            return await apiPut<CompanyResponse>(`/companies/${companyId}`, data);
        },
        onSuccess: (_data, variables) => {
            // Invalida lista e dettaglio
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.detail(variables.companyId) });
        },
    });
}

/**
 * Hook per eliminare company
 */
export function useDeleteCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (companyId: string): Promise<DeleteResponse> => {
            return await apiDelete<DeleteResponse>(`/companies/${companyId}`);
        },
        onSuccess: () => {
            // Invalida lista companies
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per validare company
 */
export function useValidateCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (companyId: string) => {
            return await apiPost(`/companies/${companyId}/validate`, {});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.all });
            queryClient.invalidateQueries({ queryKey: ['business-partner'] });
            queryClient.invalidateQueries({ queryKey: ['business-partners'] });
        },
    });
}

/**
 * Hook per rimuovere validazione company
 */
export function useUnvalidateCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (companyId: string) => {
            return await apiPost(`/companies/${companyId}/unvalidate`, {});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.all });
            queryClient.invalidateQueries({ queryKey: ['business-partner'] });
            queryClient.invalidateQueries({ queryKey: ['business-partners'] });
        },
    });
}
